import { ApolloError } from '@apollo/client';
import {
  BottomSheetBackdropProps,
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Keyboard,
  Platform,
  StyleSheet,
  TextInput,
} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Alert } from '@components/Alert';
import { DateSelect } from '@components/FormModals/DateSelect';
import ProjectSelect from '@components/FormModals/ProjectSelect';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { BottomSheetBackdrop } from '@components/Modals/BottomSheetBackdrop';
import { Box } from '@components/Restyle';
import {
  TaskCreateAttachmentsPreview,
  TaskCreateAttachmentsProvider,
  useTaskCreateAttachmentsStore,
} from '@components/Tasks/TaskCreateAttachmentsPreview';
import { dismissKeyboardBefore } from '@components/Tasks/taskCreateBottomSheetKeyboard';
import {
  computeDateModalContentHeight,
  computeDateModalSnapHeight,
  resolveOnboardingTaskSheetSnapPoints,
  resolveTaskSheetFooterBottomInset,
  resolveTaskSheetSnapPoints,
  TASK_FORM_DATE_MODAL_COLLAPSE_HEIGHT,
} from '@components/Tasks/taskCreateBottomSheetSnapPoints';
import { runTaskCreateSubmit } from '@components/Tasks/taskCreateBottomSheetSubmit';
import { TaskCreateModalFooter } from '@components/Tasks/TaskCreateModalFooter';
import {
  DESCRIPTION_LINE_HEIGHT,
  TaskCreateModalFormNew,
} from '@components/Tasks/TaskCreateModalFormNew';
import { FormValues } from '@components/Tasks/TaskFormStack';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import {
  Chat,
  LocalFile,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import { useBottomSheetDiscard } from '@hooks/useBottomSheetDiscard';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useFilePickerHandlers } from '@hooks/useFilePickerHandlers';
import { useKeyboardHeight } from '@hooks/useKeyboardHeight';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTaskCreateFooter from '@hooks/useTaskCreateFooter';
import { NEW_BOTTOM_SHEET_MAX_DOC_COUNT } from '@schemas/chatInputSchema';
import theme from '@themes/theme';
import { logApolloError } from '@utils/logNonFatalError';
import { getTaskRoleEnumValue } from '@utils/tasks';

export type TaskCreateFormStackParamList = {
  'task-create-modal-form': undefined;
  'project-select': undefined;
  'member-select': undefined;
  'date-select': { field: FormValues['startDate' | 'dueDate'] };
  'dependency-select': undefined;
};

interface TaskCreateBottomSheetComponentProps {
  parentProject?: boolean;
  isCopyTask?: boolean;
  taskname?: string;
  selectedList?: [];
  values?: FormValues;
  chatId?: Chat['id'];
  isAssignTask?: boolean;
  goBackOnComplete?: boolean;
  /** Inline create-task error banner (TA-10210) */
  errorMsg?: string;
  onDismissError?: () => void;
}

export const TaskCreateBottomSheet = forwardRef(
  (
    {
      parentProject,
      isCopyTask,
      taskname,
      selectedList: _selectedList,
      chatId,
      isAssignTask = false,
      goBackOnComplete,
      errorMsg,
      onDismissError,
    }: TaskCreateBottomSheetComponentProps,
    forwardRef
  ) => {
    const navigation = useNavigation();
    const isFocused = useIsFocused();
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    const {
      isVisible,
      setProjectTutorialStateAndInStorage,
      projectTutorialState,
      addMembersTutorialState,
      setAddMemeberTutorialStateAndInStorage,
    } = useProjectTutorial();
    const { setSelectedMembers, selectedMembers } = useSelectedMembers();
    const [modalPosition, setModalPosition] = useState(0);
    const [blankTaskForm, setBlankTaskForm] = useState(true);
    const {
      isReadySubmitting,
      setIsReadySubmitting,
      isAddAssignee,
      setIsAddAssignee,
    } = useTaskCreateFooter();
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    // Mirrors isProjectModalOpen/isDateModalOpen synchronously — the state
    // versions are still needed for childModals' useMemo below (a ref change
    // wouldn't trigger that recompute) and the onboarding-tooltip check, but
    // a guard that reads state inside dismissProjectModal/dismissDateModal
    // can be stale if the function is called twice before React commits the
    // first setState(false).
    const isProjectModalOpenRef = useRef(false);
    const isDateModalOpenRef = useRef(false);
    const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
    const [localImages, setLocalImages] = useState<LocalFile[]>([]);
    const [allFiles, setAllFiles] = useState<LocalFile[]>([]);
    const updateMember = (userId: string, role: string) => {
      const members = selectedMembers?.map((u) => {
        if (u.id !== userId) return u;
        else {
          return { ...u, role: getTaskRoleEnumValue(role) };
        }
      });
      setSelectedMembers(members);
    };

    const {
      isValid,
      submitForm,
      resetForm,
      setFieldValue,
      isSubmitting,
      values,
    } = useFormikContext<FormValues>();

    const { me } = useMe();
    const onboardingActive = useOnboardingActive();
    const [updateOnboardingState] = useUpdateOnboardingStateMutation();
    const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
    const sheetAnimatedPosition = useSharedValue(0);

    // refs
    const bottomSheetTaskFormRef = useRef<BottomSheetModal>(null);
    useImperativeHandle(forwardRef, () => bottomSheetTaskFormRef.current);
    const bottomSheetProjectModalRef = useRef<BottomSheetModal>(null);
    const bottomSheetDateModalRef = useRef<BottomSheetModal>(null);

    // variables
    const dateModalContentHeight = computeDateModalContentHeight(insets.bottom);
    const dateModalSnapHeight = computeDateModalSnapHeight(insets.bottom);
    const snapPoints = [dateModalSnapHeight, dateModalSnapHeight, '95%'];

    const inputRef = useRef<TextInput>(null);
    const pendingTooltipRef = React.useRef(false);
    // callbacks
    const handleFormSheetChange = (index: number) => {
      if (index === -1) {
        setShowOnboardingTooltip(false);
        pendingTooltipRef.current = false;
        // TA-10920 #12: only the onboarding task skips Discard; any other
        // sheet opened while onboarding is still active keeps the alert.
        if (!blankTaskForm && !noNeedShowDiscard && !values.isOnboardingTask) {
          discardAlert();
        } else {
          setModalPosition(index);
          navigation.goBack();
        }
      } else {
        setModalPosition(index);
        if (index === 1) {
          !isAddAssignee && inputRef.current?.focus();
        }
        if (pendingTooltipRef.current) {
          pendingTooltipRef.current = false;
          setShowOnboardingTooltip(true);
          setFieldValue('isOnboardingTask', true);
        }
      }
    };
    const dismissTaskFormModal = () => {
      if (bottomSheetTaskFormRef.current) {
        bottomSheetTaskFormRef.current.dismiss();
      }
    };
    const openProjectModal = () => {
      Keyboard.dismiss();
      isProjectModalOpenRef.current = true;
      setIsProjectModalOpen(true);
      if (bottomSheetProjectModalRef.current) {
        bottomSheetProjectModalRef.current.present();
      }
    };
    // `skipSheetDismiss` lets this double as the sheet's own `onDismiss`
    // handler without re-dismissing an already-closed sheet. Same ref-based
    // guard as dismissDateModal, for the same reason: a guard reading state
    // can be stale if this is called twice before React commits the first
    // setIsProjectModalOpen(false).
    const dismissProjectModal = (skipSheetDismiss = false) => {
      if (!isProjectModalOpenRef.current) return;
      isProjectModalOpenRef.current = false;
      setIsProjectModalOpen(false);
      if (!skipSheetDismiss && bottomSheetProjectModalRef.current) {
        bottomSheetProjectModalRef.current.dismiss();
      }
    };
    // Collapses the Task Form to its index 0, which initialSnapPoints swaps
    // to TASK_FORM_DATE_MODAL_COLLAPSE_HEIGHT while isDateModalOpen is true.
    // The array's LENGTH never changes, only that value, so index 0 is valid
    // whether gorhom has caught up with the re-render yet or not — its own
    // OnSnapPointsChange reaction then moves the sheet to the new value at
    // the same index (TA-10614).
    const openDateModal = () => {
      Keyboard.dismiss();
      isDateModalOpenRef.current = true;
      setIsDateModalOpen(true);
      if (bottomSheetDateModalRef.current) {
        bottomSheetDateModalRef.current.present();
      }
      bottomSheetTaskFormRef.current?.snapToIndex(0);
    };
    // Same `skipSheetDismiss` pattern as dismissProjectModal — lets this
    // double as the Date Modal's own `onDismiss` handler, covering a
    // pan-down/back dismissal as well as the explicit paths (DateSelect's
    // onSelectFinish, the backdrop's closeSheet). Only resets the open-guard
    // and state: index 0 reverts to its normal value and gorhom re-positions
    // the still-collapsed sheet there. No snapToIndex(1) restore — that fires
    // onChange(1), refocusing Name and reopening the keyboard right after
    // picking a date (human-review finding on #5819).
    const dismissDateModal = useCallback((skipSheetDismiss = false) => {
      if (!isDateModalOpenRef.current) return;
      isDateModalOpenRef.current = false;
      setIsDateModalOpen(false);
      if (!skipSheetDismiss && bottomSheetDateModalRef.current) {
        bottomSheetDateModalRef.current.dismiss();
      }
    }, []);
    // Memoized like useBottomSheetDiscard's own renderBackdrop (review
    // finding on #5819) — an inline arrow here is a new component type every
    // render, which gorhom mounts as a fresh BottomSheetBackdrop each time,
    // tearing down and rebuilding its Reanimated fade style mid-animation on
    // any unrelated re-render of this (large, frequently re-rendering) form
    // while the Date Modal happens to be open.
    const renderDateModalBackdrop = useCallback(
      (props: BottomSheetBackdropProps) => (
        <BottomSheetBackdrop {...props} closeSheet={() => dismissDateModal()} />
      ),
      [dismissDateModal]
    );

    // Latched in a layout effect so the onboarding layout (no CTA row in the
    // footer) is in place before the first paint instead of flashing for a
    // frame (TA-10920 #11). It also runs ahead of the cache patch below that
    // flips taskCreationComplete, so isOnboardingTaskSheet stays true for the
    // whole session of this sheet.
    useLayoutEffect(() => {
      if (!onboardingActive) return;
      if (me?.onboardingState?.taskCreationComplete !== false) return;
      setFieldValue('isOnboardingTask', true);
    }, [onboardingActive, me?.onboardingState?.taskCreationComplete]);

    useEffect(() => {
      if (!onboardingActive) return;
      if (me?.onboardingState?.taskCreationComplete !== false) return;
      pendingTooltipRef.current = true;
      updateOnboardingState({
        variables: { attributes: { taskCreationComplete: true } },
        update: (cache) => {
          if (!me?.id) return;
          cache.modify({
            id: cache.identify({ __typename: 'User', id: me.id }),
            fields: {
              onboardingState: (existing) => ({
                ...existing,
                taskCreationComplete: true,
              }),
            },
          });
        },
      });
    }, [
      onboardingActive,
      me?.id,
      me?.onboardingState?.taskCreationComplete,
      updateOnboardingState,
    ]);

    useEffect(() => {
      if (showOnboardingTooltip && values.name && values.name.length > 0) {
        setShowOnboardingTooltip(false);
      }
    }, [values.name, showOnboardingTooltip]);

    useEffect(() => {
      if (bottomSheetTaskFormRef.current) {
        bottomSheetTaskFormRef.current.present();
      }
    }, [bottomSheetTaskFormRef]);

    useEffect(() => {
      // setFieldValue('users', selectedMembers);
      setFieldValue(
        'users',
        selectedMembers.filter((t) => t.__typename !== 'StringSearchType')
      );
      setFieldValue(
        'inviteEmails',
        selectedMembers
          .filter((t) => t.__typename === 'StringSearchType')
          .map((t) => {
            return {
              email: t.name,
              role: t.role,
            };
          })
      );
    }, [selectedMembers]);

    useEffect(() => {
      if (localFiles.length > 0) {
        setFieldValue('files', localFiles);
      } else {
        setFieldValue('files', []);
      }
    }, [localFiles, setFieldValue]);

    useEffect(() => {
      isAddAssignee && setIsAddAssignee(false);
      !isCopyTask && setSelectedMembers([]);
      return () => {
        !isCopyTask && setSelectedMembers([]);
      };
    }, []);

    const handleComponent = () => <Box style={styles.closeLine} />;
    // The New Task form pulls its grabber 4pt closer to the Name field (QA).
    // Its own handle rather than a smaller `closeLine`, because the due-date
    // sheet below shares that one and keeps the original spacing.
    const handleComponentTaskForm = () => (
      <Box style={[styles.closeLine, styles.closeLineTaskForm]} />
    );
    const handleComponentScreen = () => <Box style={styles.closeLineScreen} />;

    const handleForm = (isFormBlank: boolean) => {
      setBlankTaskForm(isFormBlank);
    };

    const [noNeedShowDiscard, setNoNeedShowDiscard] = useState(false);

    // Reset noNeedShowDiscard when screen regains focus (e.g., after returning from add-members stack)
    useEffect(() => {
      if (isFocused) {
        setNoNeedShowDiscard(false);
      }
    }, [isFocused]);

    const childModals = useMemo(
      () => [
        { isOpen: isProjectModalOpen, dismiss: dismissProjectModal },
        { isOpen: isDateModalOpen, dismiss: dismissDateModal },
      ],
      [
        isProjectModalOpen,
        dismissProjectModal,
        isDateModalOpen,
        dismissDateModal,
      ]
    );

    const { discardAlert, renderBackdrop } = useBottomSheetDiscard({
      bottomSheetRef:
        bottomSheetTaskFormRef as React.RefObject<BottomSheetModal>,
      isBlankForm: blankTaskForm,
      noNeedShowDiscard,
      isFocused,
      onDismiss: () => navigation.goBack(),
      titleKey: 'models:discardTask.title',
      messageKey: 'models:discardTask.message',
      childModals,
    });

    const [bottomSheetFooterHeight, setBottomSheetFooterHeight] =
      useState<number>(0);

    const { keyboardHeight: rawKeyboardHeight } = useKeyboardHeight();
    // react-native-screens freezes this screen (enableFreeze) while
    // task-members-add is focused. useKeyboardHeight's Keyboard listeners
    // are global, so keyboard events meant for that other screen still
    // call setKeyboardHeight() here — React just defers applying the
    // update since the tree is frozen. Critically, useIsFocused() is
    // ALSO a hook inside this same frozen tree, so both `isFocused` and
    // the queued `rawKeyboardHeight` flip together in the single commit
    // that unfreezes this screen — deriving keyboardHeight from
    // `isFocused ? rawKeyboardHeight : 0` still leaks the stale queued
    // value at that exact instant, since isFocused is already true by
    // the time rawKeyboardHeight resolves. Instead, explicitly zero out
    // on the false→true focus transition itself (this screen never
    // refocuses a text input on return — see the isAddAssignee guard in
    // handleFormSheetChange — so the keyboard is genuinely closed) and
    // only trust rawKeyboardHeight for renders after that (TA-10210
    // follow-up; the isFocused-gate alone didn't fix the jitter).
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const wasFocusedRef = useRef(isFocused);
    useEffect(() => {
      if (isFocused && !wasFocusedRef.current) {
        setKeyboardHeight(0);
      } else if (isFocused) {
        setKeyboardHeight(rawKeyboardHeight);
      }
      wasFocusedRef.current = isFocused;
    }, [isFocused, rawKeyboardHeight]);

    // Computed outside renderFooter to avoid unnecessary callback recreation
    // on Android. The onboarding snap points read the same helper, so the
    // fixed-height onboarding sheet always makes room for the lifted footer.
    const footerBottomInset = useMemo(
      () =>
        resolveTaskSheetFooterBottomInset({
          keyboardHeight,
          bottomInset: insets.bottom,
          platform: Platform.OS,
        }),
      [keyboardHeight, insets.bottom]
    );

    const { uploadingFiles, setUploadingFiles, directUploadFiles } =
      useChatUploadManager();

    const handleUploadAndUpdate = async (
      filesToUpload: LocalFile[],
      isImage: boolean
    ) => {
      try {
        const uploaded = await directUploadFiles(filesToUpload);
        (isImage ? setLocalImages : setLocalFiles)((prev) => {
          const map = new Map(prev.map((f) => [f.clientId, f]));
          uploaded.forEach((item) => {
            if (map.has(item.clientId)) {
              const original = map.get(item.clientId)!;
              map.set(item.clientId, {
                ...original,
                blobId: item.blobId,
              });
            }
          });
          return Array.from(map.values());
        });
      } catch (error) {
        console.error('Error uploading files:', error);
      }
    };

    const showLimitReached = () => {
      Alert.alert('Limit Reached', t('models:chat.input.maxSelection64'), [
        {
          text: 'Got It',
          style: 'destructive',
          onPress: () => {},
        },
      ]);
    };

    const { pickFiles, pickImages, launchCamera } = useFilePickerHandlers({
      allFilesCount: allFiles?.length ?? 0,
      maxCount: NEW_BOTTOM_SHEET_MAX_DOC_COUNT,
      setNoNeedShowDiscard,
      setLocalFiles,
      setLocalImages,
      handleUploadAndUpdate,
      navigation,
      showLimitReached,
    });

    useEffect(() => {
      setUploadingFiles([]);
      setLocalFiles([]);
      setIsReadySubmitting(false);
      isSubmittingTask.current = false;
    }, []);

    useEffect(() => {
      const allFiles = [...localFiles, ...localImages];
      allFiles && setAllFiles(allFiles);
      allFiles && setFieldValue('files', allFiles);
    }, [localFiles, localImages]);

    // Scope uploadingFiles to this task's files using clientId matching
    const taskClientIds = useMemo(
      () => new Set((allFiles ?? []).map((f) => f.clientId).filter(Boolean)),
      [allFiles]
    );

    const taskUploadingFiles = useMemo(
      () => uploadingFiles.filter((u) => taskClientIds.has(u.file.clientId)),
      [uploadingFiles, taskClientIds]
    );

    // Use uploadingFiles for incremental progress tracking
    // A file is considered "uploaded" when it has a blobId set (even if status is still 'uploading')
    const fileStillUploading = useMemo(
      () => taskUploadingFiles.filter((u) => u.status === 'uploading'),
      [taskUploadingFiles]
    );

    const fileUploadedCount = useMemo(() => {
      const total = taskUploadingFiles.reduce((sum, item) => {
        return sum + (Number(item.progress) || 0);
      }, 0);

      return Math.floor(total);
    }, [taskUploadingFiles]);

    const totalFiles = allFiles?.length ?? 0;

    // Show loader when user has clicked submit and there are files still uploading
    const shouldShowLoader =
      isReadySubmitting && totalFiles > 0 && fileStillUploading.length > 0;

    // TA-10495 #17 (Copilot review): built at TaskCreateBottomSheet's own top
    // level, NOT inside renderFooter below — see useTaskCreateAttachmentsStore's
    // doc comment for why the store has to be created here to stay live.
    const attachmentsSnapshot = useMemo(
      () => ({
        localFiles,
        localImages,
        shouldShowLoader,
        fileUploadedCount,
        isReadySubmitting,
      }),
      [
        localFiles,
        localImages,
        shouldShowLoader,
        fileUploadedCount,
        isReadySubmitting,
      ]
    );
    const attachmentsStore = useTaskCreateAttachmentsStore(attachmentsSnapshot);

    const isSubmittingTask = useRef<boolean>(false);

    useEffect(() => {
      const uploadingFilesFailed = taskUploadingFiles.filter(
        (u) => u.status === 'error'
      );
      if (uploadingFilesFailed && uploadingFilesFailed.length > 0) {
        setIsReadySubmitting(false);
        Alert.alert(
          'TaskTag',
          t('models:files.uploadFilesError1', {
            count: uploadingFilesFailed.length,
          })
        );
      }
    }, [taskUploadingFiles]);

    useEffect(() => {
      // Only proceed if user has clicked submit and there are files
      if (!isReadySubmitting) {
        return;
      }

      const notHaveBlobIdFiles = allFiles.filter(
        (file) => !file.blobId || file.blobId === ''
      );

      const uploadingFilesFailed = taskUploadingFiles.filter(
        (u) => u.status === 'error'
      );

      if (
        allFiles.length > 0 &&
        notHaveBlobIdFiles.length !== uploadingFilesFailed.length
      ) {
        return;
      }

      if (fileStillUploading.length === 0 && !isSubmittingTask.current) {
        // All files for this task have finished uploading - submit the form
        allFiles &&
          allFiles.length > 0 &&
          (!values.files || values.files.length > 0) &&
          setFieldValue('files', allFiles);

        setTimeout(
          () => {
            isSubmittingTask.current = true;
            runTaskCreateSubmit(submitForm, {
              onSuccess: () => {
                // Reset immediately once the mutation resolves — the extra
                // 500ms here used to leave the assignee avatar/attachments
                // showing stale data for another half second after "task
                // created" (reported bug: avatar should already be back to
                // the default UserPlus circle at that point).
                setSelectedMembers([]);
                setLocalFiles([]);
                setLocalImages([]);
                setAllFiles([]);
                setBlankTaskForm(true);
                resetForm();
              },
              onFailure: (error) => {
                // createTask failed (TaskFormStack now propagates the
                // rejection instead of swallowing it) — leave the entered
                // name/description/attachments in place so the user can
                // retry from the failed state instead of losing their
                // input (TA-10210 QA fail). Still log it so a silent
                // create-task failure is visible in Crashlytics/Sentry.
                if (error instanceof ApolloError) {
                  logApolloError(error, 'TaskCreateBottomSheet createTask', []);
                }
              },
              onSettled: () => {
                setIsReadySubmitting(false);
                isSubmittingTask.current = false;
              },
            });
          },
          500 * ((allFiles.length > 20 ? allFiles.length : 20) / 20)
        );
      }
    }, [allFiles, isReadySubmitting, fileStillUploading, taskUploadingFiles]);

    const getTaskCreateModalFooter = () => (
      <TaskCreateModalFooter
        bottomSheetTaskFormRef={bottomSheetTaskFormRef}
        chatId={chatId}
        isAssignTaskToChat={isAssignTask && chatId !== ''}
        isCreateTask
        taskname={taskname}
        members={selectedMembers}
        onFormValidityChange={handleForm}
        onSave={() => {
          setIsReadySubmitting(true);
          isSubmittingTask.current = false;
        }}
        setNoNeedShowDiscard={setNoNeedShowDiscard}
        onCancel={() => {
          setSelectedMembers([]);
          dismissTaskFormModal();
        }}
        updateMember={(member, role) => updateMember(member.id, role)}
        addMembers={dismissKeyboardBefore(() => {
          if (isVisible && addMembersTutorialState !== '4') {
            setAddMemeberTutorialStateAndInStorage('1');
          }
          if (chatId) {
            navigation.navigate('add-task-modal-members', {
              chatId: chatId,
            });
          } else {
            navigation.navigate('task-add-members-stack', {
              selectedProjectId: values.projectId,
              isCreateTaskForMobile: true,
            });
          }
        })}
        onProjectPress={openProjectModal}
        onDatePress={openDateModal}
        pickFiles={dismissKeyboardBefore(pickFiles)}
        pickImages={dismissKeyboardBefore(pickImages)}
        launchCamera={dismissKeyboardBefore(launchCamera)}
        parentProject={parentProject}
      />
    );

    const renderFooter = useCallback(
      (props: BottomSheetFooterProps) => {
        return (
          <BottomSheetFooter
            bottomInset={footerBottomInset}
            style={{
              backgroundColor: 'white',
            }}
            {...props}>
            <Box
              onLayout={(e) => {
                setBottomSheetFooterHeight(e.nativeEvent.layout.height);
              }}>
              {/* Attachments are no longer here — they render inside the
                  sheet's content, under the Description, the way New Project's
                  form does. The footer is the controls row only, so it keeps a
                  fixed height however many files are attached. */}
              {getTaskCreateModalFooter()}
            </Box>

            {isVisible && projectTutorialState === '12' && (
              <Box
                position='absolute'
                bottom={135}
                style={{ right: theme.spacing.m }}>
                <TooltipPopup
                  visible={true}
                  title='Assign Members'
                  description='Feel free to delegate this task to multiple team members.'
                  pointerPosition='bottom-right'
                  onSkip={() => setProjectTutorialStateAndInStorage('13')}
                  currentStep={3}
                  totalSteps={4}
                  maxWidth={Dimensions.get('window').width - 62}
                  bottomRightPos={16}
                />
              </Box>
            )}
            {isVisible && projectTutorialState === '13' && (
              <Box
                zIndex={10}
                position='absolute'
                bottom={65}
                style={{ right: theme.spacing.m }}>
                <TooltipPopup
                  visible={true}
                  title='Create Task'
                  description='All set! You can view the Task details now'
                  pointerPosition='bottom-right'
                  currentStep={4}
                  totalSteps={4}
                  maxWidth={Dimensions.get('window').width - 15}
                  bottomRightPos={16}
                />
              </Box>
            )}
          </BottomSheetFooter>
        );
      },
      // selectedMembers intentionally excluded: @gorhom/bottom-sheet renders
      // footerComponent as a JSX type (<FooterComponent .../>), so any change
      // to renderFooter's identity remounts the whole footer subtree —
      // including TaskCreateModalFooter's project-name query — every time an
      // assignee is added/removed. TaskCreateModalFooter already reads
      // selectedMembers live from its own useSelectedMembers() hook, and the
      // add-members screen (TaskMembersAdd) does the same rather than reading
      // the members prop threaded through here, so dropping it from deps
      // loses nothing.
      //
      // localFiles/localImages/shouldShowLoader/fileUploadedCount/
      // isReadySubmitting are excluded for the same reason (TA-10495 #17,
      // Copilot review) — they used to sit here and remounted this same
      // subtree on every file pick and upload-progress tick.
      // TaskCreateAttachmentsPreview now reads them live via
      // attachmentsStore/useSyncExternalStore instead (see
      // useTaskCreateAttachmentsStore's doc comment), so dropping them from
      // deps loses nothing there either. attachmentsStore/setLocalFiles/
      // setLocalImages are included below only for lint completeness — all
      // three are referentially stable across renders, so none of them ever
      // actually trigger a recompute.
      [
        footerBottomInset,
        isVisible,
        projectTutorialState,
        addMembersTutorialState,
        attachmentsStore,
        setLocalFiles,
        setLocalImages,
      ]
    );

    // Clamped description height reported by the form. Only tracked for the
    // onboarding sheet, whose fixed height has to grow with it; elsewhere it
    // would just churn the snap points (see the note inside the memo below).
    const [descriptionHeight, setDescriptionHeight] = useState(
      DESCRIPTION_LINE_HEIGHT
    );
    const handleDescriptionHeightChange = useCallback(
      (height: number) => {
        if (!values.isOnboardingTask) return;
        setDescriptionHeight((prev) => (prev === height ? prev : height));
      },
      [values.isOnboardingTask]
    );

    // TA-10920: decided on the very first render — the sheet presents on
    // mount with whatever snap points that render produced, so waiting for
    // the open callback to set isOnboardingTask flashed the tall regular
    // sheet before it shrank to the onboarding height.
    const isOnboardingTaskSheet =
      !!values.isOnboardingTask ||
      (onboardingActive && me?.onboardingState?.taskCreationComplete === false);

    const initialSnapPoints = useMemo(() => {
      let baseSnapPoints: (number | string)[];
      // Checked before the legacy tutorial states below: '7' / '11' are also
      // set during the onboarding task flow and would reopen the tall sheet.
      if (isOnboardingTaskSheet) {
        baseSnapPoints = resolveOnboardingTaskSheetSnapPoints({
          keyboardHeight,
          bottomInset: insets.bottom,
          platform: Platform.OS,
          descriptionExtraHeight: descriptionHeight - DESCRIPTION_LINE_HEIGHT,
        });
      } else if (
        isVisible &&
        (projectTutorialState === '11' || projectTutorialState === '7')
      ) {
        baseSnapPoints =
          Platform.OS === 'android' ? ['65%', '65%'] : ['90%', '90%'];
      } else {
        baseSnapPoints = resolveTaskSheetSnapPoints({
          screenHeight: Dimensions.get('window').height,
          keyboardHeight,
          platform: Platform.OS,
        });
      }

      // While the Date Modal is open, index 0 becomes a real (configured)
      // snap point tiny enough to sit fully under it — never a
      // `snapToPosition` to an off-detent value, which gorhom treats as a real
      // close and breaks the Date Modal's stackBehavior='push' (TA-10614).
      // Swaps the VALUE at index 0 instead of prepending: gorhom's
      // snapToIndex validates against its own detents, which update on the UI
      // thread after this render, so an index that only exists in the longer
      // array (e.g. the keyboard effect's lastSnapIndex) throws
      // "'index' was provided but out of the provided snap points range".
      return isDateModalOpen
        ? [TASK_FORM_DATE_MODAL_COLLAPSE_HEIGHT, ...baseSnapPoints.slice(1)]
        : baseSnapPoints;
      // Only what the body reads. `localFiles` and the description size tier
      // used to sit here: neither is read, but both churned this array's
      // identity, and the keyboard effect below keys on it — attaching a file
      // with the keyboard down re-ran it and dropped a manually dragged sheet
      // back to its short point (Jack's review, finding 3).
    }, [
      projectTutorialState,
      isVisible,
      keyboardHeight,
      isOnboardingTaskSheet,
      insets.bottom,
      descriptionHeight,
      isDateModalOpen,
    ]);

    // Keyboard down, sheet down (TA-10495 QA round 3): with no keyboard to
    // clear, the taller point leaves a stretch of empty sheet under the form,
    // so it drops to the shorter one and comes back up when the keyboard does.
    //
    // The first run is skipped. The sheet opens at index 1 with the Name field
    // autofocused, so `keyboardHeight` is still 0 for a frame or two — acting
    // on that would shrink the sheet and immediately re-expand it, in view.
    //
    // Keyed on the index rather than the array so a new-but-equal
    // `initialSnapPoints` cannot re-trigger it.
    const lastSnapIndex = initialSnapPoints.length - 1;
    const hasSeenKeyboardRef = useRef(false);
    useEffect(() => {
      if (!isFocused) return;
      if (keyboardHeight > 0) hasSeenKeyboardRef.current = true;
      if (!hasSeenKeyboardRef.current) return;

      bottomSheetTaskFormRef.current?.snapToIndex(
        keyboardHeight > 0 ? lastSnapIndex : 0
      );
    }, [keyboardHeight, isFocused, lastSnapIndex]);

    const tooltipAnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateY: sheetAnimatedPosition.value }],
    }));

    const screenHeight = Dimensions.get('window').height;
    useAnimatedReaction(
      () => sheetAnimatedPosition.value,
      (position) => {
        if (position >= screenHeight - 40) {
          runOnJS(setShowOnboardingTooltip)(false);
        }
      }
    );

    const getBottomSheetView = () => {
      const taskCreateModalForm = (
        <TaskCreateModalFormNew
          bottomSheetTaskFormRef={bottomSheetTaskFormRef}
          onDescriptionContentSizeChange={handleDescriptionHeightChange}
          chatId={chatId}
          isAssignTaskToChat={isAssignTask && chatId !== ''}
          textInputRef={inputRef}
          isCreateTask
          taskname={taskname}
          members={selectedMembers}
          goBackOnComplete={goBackOnComplete}
          onFormValidityChange={handleForm}
          disabled={!isValid || isSubmitting}
          onCancel={() => {
            setSelectedMembers([]);
            dismissTaskFormModal();
          }}
          isReadySubmitting={isReadySubmitting}
          errorMsg={errorMsg}
          onDismissError={onDismissError}
        />
      );

      // Attachments sit under the Description, inside the sheet's own scroll
      // content — the same place ProjectCreateModalForm renders them. They
      // scroll with the form instead of growing the floating footer, so a long
      // list can no longer take height away from the fields above it.
      //
      // The provider used to have to live inside the footer's @gorhom/portal
      // tree; here it is in the ordinary tree, so it simply wraps the content.
      const taskCreateModalContent = (
        <TaskCreateAttachmentsProvider
          store={attachmentsStore}
          setLocalFiles={setLocalFiles}
          setLocalImages={setLocalImages}>
          {taskCreateModalForm}
          <TaskCreateAttachmentsPreview />
        </TaskCreateAttachmentsProvider>
      );

      if (Platform.OS === 'ios') {
        return (
          <BottomSheetScrollView
            keyboardShouldPersistTaps='handled'
            contentContainerStyle={{
              flexGrow: 1,
              // Math.max(keyboardHeight, insets.bottom): BottomSheetScrollView
              // doesn't itself account for keyboard height, so without this
              // the Description field could still scroll to a position the
              // keyboard covers even after the sheet repositions.
              paddingBottom:
                (bottomSheetFooterHeight ?? 136) +
                Math.max(keyboardHeight, insets.bottom),
            }}>
            <BottomSheetView style={{ flex: 1 }}>
              {taskCreateModalContent}
            </BottomSheetView>
          </BottomSheetScrollView>
        );
      } else {
        return (
          <BottomSheetScrollView>
            <BottomSheetView>
              {taskCreateModalContent}
              <Box
                height={
                  (bottomSheetFooterHeight ?? 136) +
                  Math.max(keyboardHeight, insets.bottom)
                }
              />
            </BottomSheetView>
          </BottomSheetScrollView>
        );
      }
    };

    const getTooltipBottom = (type: 'name' | 'description') => {
      if (Platform.OS !== 'android') {
        return type === 'name' ? 580 : 550;
      }

      if (keyboardHeight > 0) {
        return type === 'name'
          ? (Dimensions.get('window').height - keyboardHeight) * 0.3 + 120
          : (Dimensions.get('window').height - keyboardHeight) * 0.3 + 170;
      }

      return type === 'name'
        ? Dimensions.get('window').height * 0.3 + 140
        : Dimensions.get('window').height * 0.3 + 190;
    };

    const [showTutorialTooltip, setShowTutorialTooltip] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        setShowTutorialTooltip(true);
      }, 500);
      return () => clearTimeout(timer);
    }, []);

    return (
      <>
        <Animated.View
          pointerEvents='box-none'
          style={[
            {
              position: 'absolute',
              left: theme.spacing.l,
              top: -theme.spacing.xl,
              zIndex: 99999,
            },
            tooltipAnimatedStyle,
          ]}>
          <TooltipPopup
            isFromChat
            animate={true}
            fadeTime={2000}
            visible={
              onboardingActive && showOnboardingTooltip && !isProjectModalOpen
            }
            title={t('models:tasks.tooltip.nameTitle')}
            description={t('models:tasks.tooltip.nameSubtitle')}
            pointerPosition='bottom-left'
          />
        </Animated.View>
        <BottomSheetModal
          // Expo's default windowSoftInputMode is 'adjustResize' (this app
          // never overrides android.softwareKeyboardLayoutMode) — the real
          // native window already resizes for the keyboard. Telling gorhom
          // 'adjustPan' here was a lie: its own interactive keyboardBehavior
          // only skips double-compensating when told 'adjustResize'
          // (@gorhom/bottom-sheet BottomSheet.tsx:846-854), so it was
          // re-shifting the sheet on top of a window the OS already
          // resized — the actual cause of the Android keyboard-overlap and
          // jitter, not anything freeze/state-related.
          android_keyboardInputMode='adjustResize'
          name='Task Form'
          // Opens at the taller of the two points, as New Project's sheet
          // does — its snap points and this index are one setting, so taking
          // the points without the index would land Android on 70% where
          // New Project sits at 85%.
          index={1}
          enablePanDownToClose
          ref={bottomSheetTaskFormRef}
          onDismiss={() => {
            if (
              isVisible &&
              Number(projectTutorialState) > 10 &&
              Number(projectTutorialState) < 14
            ) {
              setProjectTutorialStateAndInStorage('11');
            }
          }}
          snapPoints={initialSnapPoints}
          maxDynamicContentSize={800}
          // 'extend' (previously iOS-only) just snaps to the tallest detent
          // and has zero awareness of keyboard height — only 'interactive'
          // actually subtracts the keyboard height from the sheet's position
          // (@gorhom/bottom-sheet BottomSheet.tsx), which is why the
          // keyboard was covering the Description field/footer on iOS.
          keyboardBehavior='interactive'
          keyboardBlurBehavior='restore'
          // New Project keeps this off unconditionally. It has to be off for
          // the snap points above to decide the height at all — with it on,
          // Android sized itself to its content instead, which is exactly the
          // difference between the two sheets.
          enableDynamicSizing={false}
          handleComponent={handleComponentTaskForm}
          footerComponent={renderFooter}
          backdropComponent={renderBackdrop}
          animatedPosition={sheetAnimatedPosition}
          onChange={handleFormSheetChange}>
          {getBottomSheetView()}
        </BottomSheetModal>

        {showTutorialTooltip &&
          isVisible &&
          (projectTutorialState === '11' || projectTutorialState === '7') &&
          values.name?.length <= 0 && (
            <Box
              position='absolute'
              top={
                Platform.OS === 'android' ? getTooltipBottom('name') : undefined
              }
              bottom={
                Platform.OS === 'ios' ? getTooltipBottom('name') : undefined
              }
              left={31}
              right={31}
              marginVertical='xxs'
              zIndex={999999}>
              <TooltipPopup
                visible={true}
                title={t('models:tasks.tooltip.nameTitle')}
                description={t('models:tasks.tooltip.nameTutorialSubtitle')}
                pointerPosition='top-left'
                currentStep={1}
                totalSteps={4}
                maxWidth={Dimensions.get('window').width}
                topLeftPos={16}
              />
            </Box>
          )}
        {showTutorialTooltip &&
          isVisible &&
          (projectTutorialState === '11' || projectTutorialState === '7') &&
          values.name?.length !== 0 &&
          (!values.description || values.description?.length <= 0) && (
            <Box
              position='absolute'
              top={
                Platform.OS === 'android'
                  ? getTooltipBottom('description')
                  : undefined
              }
              bottom={
                Platform.OS === 'ios'
                  ? getTooltipBottom('description')
                  : undefined
              }
              left={31}
              right={31}
              zIndex={999999}>
              <TooltipPopup
                visible={true}
                title='Add Description'
                description='Add details your team might need to complete the task.'
                pointerPosition='top-left'
                onSkip={() => setProjectTutorialStateAndInStorage('12')}
                currentStep={2}
                totalSteps={4}
                maxWidth={Dimensions.get('window').width}
                topLeftPos={16}
              />
            </Box>
          )}

        <BottomSheetModal
          android_keyboardInputMode='adjustResize'
          name='Project Modal'
          index={modalPosition}
          stackBehavior='push'
          enablePanDownToClose
          ref={bottomSheetProjectModalRef}
          snapPoints={['100%', '100%', '100%']}
          keyboardBehavior={Platform.OS === 'ios' ? 'extend' : 'interactive'}
          keyboardBlurBehavior='restore'
          enableDynamicSizing={false}
          enableContentPanningGesture={false}
          enableHandlePanningGesture={false}
          handleComponent={handleComponentScreen}
          // gorhom calls onDismiss after every dismissal, including this
          // sheet's own programmatic .dismiss() from dismissProjectModal —
          // the ref guard there makes that a safe no-op.
          onDismiss={() => dismissProjectModal(true)}>
          <>
            <SecondaryHeader
              title='Select a Project'
              topWrap={false}
              isProject
              dismissProject={dismissProjectModal}
            />
            <Box flex={1}>
              <ProjectSelect
                isBottomSheet
                onSelectFinish={dismissProjectModal}
              />
            </Box>
          </>
        </BottomSheetModal>

        <BottomSheetModal
          android_keyboardInputMode='adjustResize'
          name='Date Modal'
          index={1}
          // 'push' leaves the Task Form sheet mounted as-is underneath —
          // openDateModal/dismissDateModal collapse it manually instead (see
          // those functions). gorhom's own DEFAULT stackBehavior='switch'
          // (its minimize/restore) looked correct in review but left the app
          // unresponsive to taps after this sheet closed on a real Android
          // device (TA-10614 QA finding).
          stackBehavior='push'
          enablePanDownToClose
          ref={bottomSheetDateModalRef}
          snapPoints={snapPoints}
          keyboardBehavior={Platform.OS === 'ios' ? 'extend' : 'interactive'}
          keyboardBlurBehavior='restore'
          enableDynamicSizing={false}
          handleComponent={handleComponent}
          backdropComponent={renderDateModalBackdrop}
          onDismiss={() => dismissDateModal(true)}>
          <Box height={dateModalContentHeight}>
            <DateSelect
              isBottomSheet
              shouldHugContent
              onSelectFinish={() => dismissDateModal()}
              formField='dueDate'
            />
          </Box>
        </BottomSheetModal>
      </>
    );
  }
);

const styles = StyleSheet.create({
  closeLine: {
    alignSelf: 'center',
    width: 56,
    height: 4,
    borderRadius: 2.5,
    backgroundColor: theme.colors.grey03,
    margin: theme.spacing.l,
  },
  // Overlay on closeLine: 4pt less gap under the grabber, so the Name field
  // sits that much higher. Written off the spacing scale rather than as a bare
  // 20 so the relationship to `margin: spacing.l` above stays readable.
  closeLineTaskForm: {
    // TA-10920: 16pt above the grabber instead of closeLine's 24.
    marginTop: theme.spacing.m,
    marginBottom: theme.spacing.l - theme.spacing.xxs,
  },

  closeLineScreen: {
    margin: theme.spacing.l,
  },
});

// STACK MODALS EXAMPLE -> https://github.com/gorhom/react-native-bottom-sheet/blob/master/example/app/src/screens/modal/StackExample.tsx
